import Button from "@components/Button";
import CircularLoader from "@components/CircularLoader";
import ScalableWrapper from "@components/ScalableWrapper";
import View from "@components/View";
import { InviteRewardTypes } from "@playroomkit/pr-wars-data";
import {
  SendPlayerAllAccessStatusEvent,
  WarsEvents,
} from "@playroomkit/pr-wars/events";
import { useManager } from "@src/contexts/WarsContext";
import { images } from "@utils/media";
import { useEffect } from "react";
import { useState } from "react";

const InvitePlayersPopup = (props: { closePopup: () => void }) => {
  const { closePopup } = props;
  const manager = useManager();

  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [hasAllAccess, setHasAllAccess] = useState(false);
  const [showAllAccessClaimStatus, setShowAllAccessClaimStatus] =
    useState(false);
  const [relationships, setRelationships] = useState<
    | null
    | {
        id: string;
        name: string;
        avatar: string | null | undefined;
        status: string | null | undefined;
        loading?: boolean;
        sent?: boolean;
      }[]
  >([]);

  useEffect(() => {
    const allAccessStatus = !!manager.getMyPlayer()?.allAccess;
      setHasAllAccess(allAccessStatus);

    const handlePlayerAllAccessStatus = (event: Event) => {
      const e = event as SendPlayerAllAccessStatusEvent;
      setHasAllAccess(!!e.allAccess);
    };

    const handleStateSyncEvent = (event: Event) => {
      const allAccessStatus = !!manager.getMyPlayer()?.allAccess;
      setHasAllAccess(allAccessStatus);
    };

    document.addEventListener(WarsEvents.StateSyncEvent, handleStateSyncEvent);
    document.addEventListener(
      WarsEvents.SendPlayerAllAccessStatusEvent,
      handlePlayerAllAccessStatus
    );

    return () => {
      document.removeEventListener(
        WarsEvents.StateSyncEvent,
        handleStateSyncEvent
      );
      document.removeEventListener(
        WarsEvents.SendPlayerAllAccessStatusEvent,
        handlePlayerAllAccessStatus
      );
    };
  }, []);

  useEffect(() => {
    manager.getRelationships().then((relationships) => {
      if (relationships) {
        setRelationships(relationships);
      }
    });
  }, []);

  const inviteMoreFriends = async () => {
    await manager.invitePlayersOnDiscord();
  };

  const sendInvite = async (playerId: string) => {
    try {
      setIsSendingInvite(true);
      
      setRelationships((relationships) => {
        if (!relationships) return null;
        return relationships?.map((relationship) => {
          if (relationship.id === playerId) {
            relationship.loading = true;
          }
          return relationship;
        });
      });

      const inviteRewards = manager.getInviteRewards();

      const reward = inviteRewards?.find(
        (reward) => reward.key === InviteRewardTypes["7_DAYS_ALL_ACCESS"]
      );

      const inviteFriendResponse = await manager.inviteFriend(
        playerId,
        reward?.claimed || hasAllAccess ? undefined : reward?.id
      );

      if (inviteFriendResponse.success) {
        setShowAllAccessClaimStatus(!!inviteFriendResponse.reward?.claimed);
      }

      setRelationships((relationships) => {
        if (!relationships) return null;
        return relationships?.map((relationship) => {
          if (relationship.id === playerId) {
            relationship.loading = false;
            relationship.sent = true;
          }
          return relationship;
        });
      });
    } catch (error) {
      console.error("Failed to invite friend");
    } finally {
      setIsSendingInvite(false);
    }
  };

  const claimReward = () => {
    setShowAllAccessClaimStatus(false);
    closePopup();
  };

  return (
    <View className="w-screen h-dvh absolute top-0 left-0 z-[999999] font-[Slackey]">
      <View
        enableEventCapturing
        className="w-full h-full absolute top-0 left-0 bg-black/60"
      />
      <ScalableWrapper maintainAspectRatioBy="height">
        {showAllAccessClaimStatus ? (
          <View
            enableEventCapturing
            className="w-full h-full flex items-center justify-center"
          >
            <Button
              onClick={claimReward}
              className="w-[24rem] h-[24rem] bg-transparent border-none outline-none animate-slide-up [animation-delay:0.3s]"
            >
              <img
                src={images.AllAccessTrialIcon}
                alt="all-access-trial-icon"
                className="w-full h-full object-contain"
              />
            </Button>
          </View>
        ) : (
          <View className="w-full h-full flex items-center justify-center">
            <View
              enableEventCapturing
              className="relative bg-[#F72EC5] w-[30rem] p-1.5 rounded-xl flex flex-col items-center"
            >
              <Button
                className="bg-transparent border-none outline-none absolute -top-4 -right-4"
                onClick={closePopup}
              >
                <img
                  src={images.ClosePopupIcon}
                  alt="close-popup"
                  className="w-8 h-8 object-contain"
                />
              </Button>
              <View className="w-full flex items-center justify-center gap-2">
                <img
                  src={images.DiscordIcon}
                  alt="discord-icon"
                  className="w-10 h-10 object-contain mb-2"
                />
                <h1
                  className="text-white mt-0.5 mb-2"
                  style={{ textShadow: "0px 3.1px 0px rgba(0, 0, 0, 1)" }}
                >
                  Share with Friends
                </h1>
              </View>
              <View className="w-full h-[28rem] bg-gradient-to-t from-[#3355FF] to-[#5B7CFF] rounded-b-xl">
                <View
                  className="w-full h-full bg-cover bg-no-repeat flex flex-col items-center justify-center gap-4"
                  style={{ backgroundImage: `url(${images.TilesBackground})` }}
                >
                  <View
                    className="w-full h-full flex flex-col py-8 px-6 gap-4 overflow-y-auto overflow-x-hidden no-scrollbar"
                    enableEventCapturing
                  >
                    {relationships?.map((relationship) => (
                      <View
                        key={relationship.id}
                        className="w-full flex items-center gap-6"
                      >
                        <View
                          className="w-14 h-14 rounded-full overflow-hidden border-4 border-solid border-[#FFB218] flex-none"
                          style={{
                            boxShadow: "0px 4px 0px 0px #F72EC5",
                          }}
                        >
                          <img
                            src={relationship.avatar ?? ""}
                            alt="avatar"
                            className="w-full h-full object-cover"
                          />
                        </View>
                        <h1 className="text-white text-2xl truncate pr-8 flex-1">
                          {relationship.name}
                        </h1>
                        <Button
                          className={`px-5 py-1.5 ${
                            relationship.sent
                              ? "bg-slate-400 border-none text-white"
                              : "bg-[#FFED45] border-4 border-solid border-[#FFB218] text-black "
                          } text-lg font-bold outline-none rounded-xl font-[Slackey]`}
                          style={{
                            boxShadow: relationship.sent
                              ? "none"
                              : "0px 4px 0px 0px #F72EC5",
                          }}
                          onClick={() => sendInvite(relationship.id)}
                          disabled={isSendingInvite}
                        >
                          {relationship.loading && (
                            <CircularLoader className="mr-3 -ml-1 size-4 -mb-0.5" />
                          )}
                          <span>{relationship.sent ? "Sent" : "Share"}</span>
                        </Button>
                      </View>
                    ))}
                  </View>
                  <View className="w-full flex items-center justify-center pb-4">
                    <Button
                      className="self-center px-5 py-1.5 bg-[#20F8B9] border-4 border-solid border-[#0EBDD0] text-black text-lg font-bold outline-none rounded-xl font-[Slackey]"
                      style={{ boxShadow: "0px 4px 0px 0px #F72EC5" }}
                      onClick={inviteMoreFriends}
                    >
                      Invite More Friends
                    </Button>
                  </View>
                </View>
              </View>
            </View>
          </View>
        )}
      </ScalableWrapper>
    </View>
  );
};

export default InvitePlayersPopup;
